/**
 * C3 教师端班级覆盖热图 (docs/UX.md §3.3).
 *
 * 行 = 学生，列 = 采集环节 (T2 单字 … T7 听辨) 或混淆类；每格 `已完成/应完成`，
 * 有被质检驳回的还带一个「需重录」标记。点一格 → 跳到质检页，已按这个学生 +
 * 这个环节筛好。
 *
 * 可及性（DESIGN_SYSTEM.md 视觉规则）：
 * - **绝不只靠颜色**。每一格里都印着 `已完成/应完成` 的数字，驳回数另起一行，
 *   颜色只是同一条信息的第二层编码；灰度打印或色盲视角下信息不丢。
 * - 每格有 `aria-label`，把「谁 · 哪个环节 · 完成几条 · 驳回几条」读全。
 * - 表格是真的 `<table>`：`<th scope="col">` / `<th scope="row">`，读屏器按行列播报。
 *
 * 手机（docs/UX.md 移动优先）：横向滚动锁在 `.xz-heat__scroll` 这个容器内部，
 * 页面本身不产生横向滚动；第一列 `position: sticky` 贴住，滚动时还知道是谁。
 */
import { useMemo, useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { EmptyState, Icon, SegmentedControl } from "@xinghai/ui";
import { teacherApi } from "../../lib/api";
import { qk } from "../../lib/queries";
import { ErrorNotice, LoadingBlock, Section } from "./common";
import type { CoverageAxis, CoverageCell, CoverageResponse, CoverageRow } from "./types";

const AXIS_OPTIONS: Array<{ value: CoverageAxis; label: string }> = [
  { value: "stage", label: "按环节" },
  { value: "confusion", label: "按混淆类" },
];

/**
 * 五档热度，只在**有分母**时才有意义。
 * `none` = 没派发（真正的空白），与「派了但一条没录」是两件事。
 */
export type HeatLevel = "none" | "zero" | "low" | "mid" | "high" | "done";

export function heatLevel(cell: CoverageCell): HeatLevel {
  if (cell.assigned === 0) return "none";
  const ratio = cell.completed / cell.assigned;
  if (ratio <= 0) return "zero";
  if (ratio >= 1) return "done";
  if (ratio < 0.34) return "low";
  if (ratio < 0.67) return "mid";
  return "high";
}

/** 读屏器读的那句话 —— 颜色之外的完整信息。 */
export function cellLabel(row: CoverageRow, columnLabel: string, cell: CoverageCell): string {
  if (cell.assigned === 0) return `${row.display_name} · ${columnLabel}：没有派发任务`;
  const base = `${row.display_name} · ${columnLabel}：已完成 ${cell.completed} / 共 ${cell.assigned}`;
  return cell.qc_rejected > 0 ? `${base}，其中 ${cell.qc_rejected} 条被质检驳回，需要重录` : base;
}

export interface CoverageHeatmapProps {
  /** Course team / admin pass the class they are looking at. */
  classId?: string | undefined;
  /** Rendered without the surrounding `Section` when false. */
  framed?: boolean;
}

export function CoverageHeatmap({ classId, framed = true }: CoverageHeatmapProps) {
  const [axis, setAxis] = useState<CoverageAxis>("stage");

  const q = useQuery({
    queryKey: qk.teacherCoverage(classId, axis),
    queryFn: ({ signal }) =>
      teacherApi.coverage({ ...(classId ? { class_id: classId } : {}), by: axis }, signal),
  });

  const body = (
    <>
      <div className="xz-adm__toolbar">
        <SegmentedControl<CoverageAxis>
          label="热图的列"
          options={AXIS_OPTIONS}
          value={axis}
          onChange={setAxis}
        />
      </div>
      {q.isPending ? (
        <LoadingBlock rows={3} label="正在加载覆盖热图" />
      ) : q.isError ? (
        <ErrorNotice error={q.error} onRetry={() => void q.refetch()} title="覆盖热图没能加载" />
      ) : (
        <HeatmapTable data={q.data} />
      )}
    </>
  );

  if (!framed) return body;
  return (
    <Section
      title="覆盖热图"
      // `.xz-adm` is a flex column, and a flex item's default `min-width:auto`
      // would let the wide table push the whole page sideways on a phone.
      className="xz-section--shrink"
      subtitle="每个学生在每个环节完成了多少。格子里是「已完成/应完成」，点一格看那几条录音。"
    >
      {body}
    </Section>
  );
}

// --------------------------------------------------------------------------- //

export function HeatmapTable({ data }: { data: CoverageResponse }) {
  const labelOf = useMemo(() => new Map(data.columns.map((c) => [c.key, c.label])), [data.columns]);

  if (data.rows.length === 0) {
    return (
      <EmptyState
        title="还没有学生"
        description="学生用班级邀请码注册、签完授权后就会出现在这里。"
      />
    );
  }

  return (
    <div className="xz-heat">
      {/*
        The scroll container owns the horizontal overflow, so the page itself
        never scrolls sideways on a phone. `tabindex` + `role=group` make the
        scrollable region reachable from the keyboard (WCAG 2.1.1).
      */}
      <div className="xz-heat__scroll" role="group" aria-labelledby="xz-heat-cap" tabIndex={0}>
        <table className="xz-heat__table">
          <caption id="xz-heat-cap" className="xz-sr-only">
            班级覆盖热图：每行一个学生，每列一个
            {data.axis === "stage" ? "采集环节" : "混淆类"}，格子里是已完成 / 应完成。
          </caption>
          <thead>
            <tr>
              <th scope="col" className="xz-heat__corner">
                学生
              </th>
              {data.columns.map((c) => (
                <th key={c.key} scope="col" className="xz-heat__colhead">
                  <span aria-hidden="true">{c.short}</span>
                  <span className="xz-sr-only">{c.label}</span>
                </th>
              ))}
              <th scope="col" className="xz-heat__colhead">
                <span aria-hidden="true">合计</span>
                <span className="xz-sr-only">合计</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => (
              <tr key={row.speaker_id}>
                <th scope="row" className="xz-heat__rowhead">
                  <span className="xz-heat__name">{row.display_name}</span>
                  {!row.verified && <span className="gz-caption">待核验</span>}
                </th>
                {row.cells.map((cell) => (
                  <HeatCell
                    key={cell.column}
                    row={row}
                    cell={cell}
                    columnLabel={labelOf.get(cell.column) ?? cell.column}
                    axis={data.axis}
                  />
                ))}
                <td className="xz-heat__total">
                  <span className="xz-heat__count">
                    {row.completed}/{row.assigned}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="xz-heat__legend">
        <span>
          <span className="xz-heat__swatch" data-level="zero" aria-hidden="true" />
          还没开始
        </span>
        <span>
          <span className="xz-heat__swatch" data-level="mid" aria-hidden="true" />
          录了一半
        </span>
        <span>
          <span className="xz-heat__swatch" data-level="done" aria-hidden="true" />
          已完成
        </span>
        <span>
          <Icon id="ui-refresh" size={16} />
          需重录（质检驳回）
        </span>
        <span className="gz-caption">
          共 {data.totals.students} 人，已完成 {data.totals.completed} / {data.totals.assigned}
        </span>
      </p>
    </div>
  );
}

function HeatCell({
  row,
  cell,
  columnLabel,
  axis,
}: {
  row: CoverageRow;
  cell: CoverageCell;
  columnLabel: string;
  axis: CoverageAxis;
}) {
  const level = heatLevel(cell);
  const label = cellLabel(row, columnLabel, cell);

  // 没派发就没有可钻取的条目，渲染成一个静态的「—」
  if (cell.assigned === 0) {
    return (
      <td className="xz-heat__cell" data-level={level}>
        <span className="xz-heat__count" aria-hidden="true">
          —
        </span>
        <span className="xz-sr-only">{label}</span>
      </td>
    );
  }

  // 混淆类没法直接映射成质检页的筛选，所以只有按环节时才可点
  const to =
    axis === "stage"
      ? `/m/qc?speaker_id=${encodeURIComponent(row.speaker_id)}&task=${encodeURIComponent(cell.column)}&all_states=1`
      : null;

  const inner = (
    <>
      <span className="xz-heat__count" aria-hidden="true">
        {cell.completed}/{cell.assigned}
      </span>
      {cell.qc_rejected > 0 && (
        <span className="xz-heat__flag" aria-hidden="true">
          <Icon id="ui-refresh" size={16} />
          {cell.qc_rejected}
        </span>
      )}
      <span className="xz-sr-only">{label}</span>
    </>
  );

  return (
    <td className="xz-heat__cell" data-level={level} data-rejected={cell.qc_rejected > 0}>
      {to ? (
        <Link className="xz-heat__link" to={to} aria-label={`${label}。查看这些录音`}>
          {inner}
        </Link>
      ) : (
        inner
      )}
    </td>
  );
}
